/**
 * The plain words for where a binding assignment's readback stands. The view
 * `commitment_assignment_readback_v` (migration 1024) decides the state; the
 * Registry page and the public API say it with these words, so both say the
 * same thing. Never "aligned": the record can show that the words match, not
 * that people agree.
 *
 * No imports: this file runs in the browser and in an edge function.
 */

export type ReadbackState = 'read_back' | 'does_not_apply' | 'unresolved';
export type ReadbackOpenReason = 'no_readback' | 'terms_changed' | 'parties_differ';

const OPEN_LINES: Record<ReadbackOpenReason, string> = {
  no_readback: 'Binding, readback unresolved: nobody has said what they will do differently.',
  terms_changed: 'Binding, readback unresolved: the terms changed after the last readback.',
  parties_differ: 'Binding, readback unresolved: the people bound said different things.',
};

/** The line for one assignment; null when nothing is open. */
export function readbackLine(state: string | null | undefined, reason: string | null | undefined): string | null {
  if (state !== 'unresolved') return null;
  return OPEN_LINES[reason as ReadbackOpenReason] ?? OPEN_LINES.no_readback;
}

/** The count, for the coverage line; null when none is open. */
export function readbackCoverageLine(
  unresolved: number,
  binding: number,
  scope: 'commitment' | 'workspace',
): string | null {
  if (unresolved <= 0) return null;
  const lead = scope === 'commitment' ? 'Readback' : 'Readback across the Registry';
  return `${lead} unresolved on ${unresolved} of ${binding} binding ${binding === 1 ? 'assignment' : 'assignments'}.`;
}
